import { useState } from "react";
import TestQuestion from "./TestQuestion";
import { SignUpButton } from "@clerk/remix";

const SAMPLE_QUESTIONS = [
  {
    id: 1,
    questionText:
      "Which concept refers to the ability of a system to increase or decrease its resource capacity based on demand?",
    questionType: "multiple_choice" as const,
    options: [
      { id: 1, text: "Availability" },
      { id: 2, text: "Elasticity" },
      { id: 3, text: "Durability" },
      { id: 4, text: "Reliability" },
    ],
    correctAnswerIds: [2],
    explanation:
      "Elasticity refers to the ability to acquire resources as you need them and release resources when you no longer need them. Think of elasticity like a rubber band - it can stretch when you need more resources and contract when you need fewer. This is different from scalability, which is about handling increased load by adding resources in a predefined manner.",
  },
  {
    id: 2,
    questionText:
      "Which AWS services can help monitor and detect security threats? (Select TWO)",
    questionType: "multiple_response" as const,
    options: [
      { id: 1, text: "AWS Direct Connect" },
      { id: 2, text: "Amazon GuardDuty" },
      { id: 3, text: "AWS CloudTrail" },
      { id: 4, text: "Amazon RDS" },
      { id: 5, text: "AWS Elastic Beanstalk" },
    ],
    correctAnswerIds: [2, 3],
    explanation:
      "Amazon GuardDuty provides intelligent threat detection, while AWS CloudTrail logs API activity for security analysis. Together they provide comprehensive security monitoring. Think of them like a security camera system (GuardDuty) combined with a visitor log book (CloudTrail).",
  },
  {
    id: 3,
    questionText:
      "Which AWS service should a company use to distribute their application content globally with minimal latency?",
    questionType: "multiple_choice" as const,
    options: [
      { id: 1, text: "Amazon CloudFront" },
      { id: 2, text: "AWS Direct Connect" },
      { id: 3, text: "Amazon VPC" },
      { id: 4, text: "Amazon Route 53" },
    ],
    correctAnswerIds: [1],
    explanation:
      "Amazon CloudFront is a content delivery network (CDN) that delivers content through a worldwide network of edge locations. Think of it like having copies of your store's most popular items in local branches around the world, so customers can get what they need from the nearest location instead of everything being shipped from a central warehouse.",
  },
];

const SampleQuestions = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [submittedAnswer, setSubmittedAnswer] = useState<number | number[]>();
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean>();
  const [isFinished, setIsFinished] = useState(false);

  const handleAnswerSelect = (answerId: number | number[]) => {
    setSubmittedAnswer(answerId);
  };

  const handleSubmitAnswer = () => {
    const currentQuestion = SAMPLE_QUESTIONS[currentQuestionIndex];
    if (Array.isArray(submittedAnswer)) {
      setIsCorrect(
        submittedAnswer.length === currentQuestion.correctAnswerIds.length &&
          submittedAnswer.every((id) =>
            currentQuestion.correctAnswerIds.includes(id)
          )
      );
    } else {
      setIsCorrect(
        currentQuestion.correctAnswerIds.includes(submittedAnswer || -1)
      );
    }
    setIsAnswered(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < SAMPLE_QUESTIONS.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSubmittedAnswer(undefined);
      setIsAnswered(false);
      setIsCorrect(undefined);
    } else {
      setIsFinished(true);
    }
  };

  if (isFinished) {
    return (
      <div className="flex flex-col items-center justify-center space-y-6 max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg">
        <h3 className="text-2xl font-bold text-center">
          Ready to access all 500+ questions?
        </h3>
        <p className="text-lg text-gray-600 text-center">
          This was just a sample of our comprehensive question bank. Get access
          to all questions, detailed explanations, and performance tracking.
        </p>
        <SignUpButton mode="modal">
          <button className="inline-flex items-center justify-center rounded-md text-xl font-medium bg-blue-600 text-white hover:bg-blue-700 h-12 px-8">
            Start Full Practice Test
          </button>
        </SignUpButton>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center w-full">
      <TestQuestion
        question={SAMPLE_QUESTIONS[currentQuestionIndex]}
        examTitle="Sample AWS Cloud Practitioner Questions"
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={SAMPLE_QUESTIONS.length}
        submittedAnswer={submittedAnswer}
        isAnswered={isAnswered}
        isCorrect={isCorrect}
        attemptId={0}
        examId={0}
        hasPaid={true}
        onNextQuestion={handleNextQuestion}
        onAnswerSelect={handleAnswerSelect}
        onSubmitAnswer={handleSubmitAnswer}
      />
    </div>
  );
};

export default SampleQuestions;
